/**
 * `PREFLIGHT` step. Snapshots the daily-stop count for the route(s) the
 * intent touches into a `PreflightReport` for downstream verifier checks
 * (count drift, full-route resequence count match). `change-day-of-week`
 * is master-only — no snapshot.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { StepResult } from '../types'
import type { RouteId } from 'types/entities/shared/ids'
import type { Deps, PipelineStep, PreflightReport, RouteOpError, RouteOpIntent } from 'types/modules/routing'

export interface PreflightStepArgs {
  readonly intent: RouteOpIntent
  readonly policy?: 'strict' | 'lenient'
}

export type PreflightStepResult = StepResult<'PREFLIGHT', PreflightReport>

const STEP_ID = 'preflight'

interface Snapshot {
  readonly counts: ReadonlyMap<RouteId, number>
  readonly errors: ReadonlyArray<RouteOpError>
  readonly warnings: ReadonlyArray<string>
}

const EMPTY_SNAPSHOT: Snapshot = {
  counts: new Map<RouteId, number>(),
  errors: [],
  warnings: [],
}

const snapshotDailyCount = async (routeId: RouteId, serviceDate: string, deps: Deps): Promise<Snapshot> => {
  const counts = new Map<RouteId, number>()
  const warnings: string[] = []
  try {
    const stops = await deps.getDailyStops({ routeId, serviceDate }).unwrap()
    counts.set(routeId, stops.length)
  } catch (cause: unknown) {
    const { message } = extractBackendError(cause)
    warnings.push(`getDailyStops failed during preflight: ${message}`)
  }
  return { counts, errors: [], warnings }
}

const snapshotForIntent = async (intent: RouteOpIntent, deps: Deps): Promise<Snapshot> => {
  switch (intent.kind) {
    case 'unassign':
    case 'assign':
    case 'resequence':
    case 'complete':
      return snapshotDailyCount(intent.routeId, intent.serviceDate, deps)
    case 'move':
      return snapshotDailyCount(intent.sourceRouteId, intent.serviceDate, deps)
    case 'change-date':
      return snapshotDailyCount(intent.routeId, intent.fromServiceDate, deps)
    case 'change-day-of-week':
      return EMPTY_SNAPSHOT
    default:
      return EMPTY_SNAPSHOT
  }
}

export const makePreflightStep = (args: PreflightStepArgs, deps: Deps): PipelineStep<'PREFLIGHT', PreflightStepResult> => {
  return {
    id: STEP_ID,
    phase: 'PREFLIGHT',
    label: 'preflight checks',
    applyOrder: 0,
    run: async () => {
      const { counts, errors, warnings } = await snapshotForIntent(args.intent, deps)
      const report: PreflightReport = {
        snapshotCounts: counts,
        warnings,
        errors,
      }
      if ((args.policy ?? 'strict') === 'strict' && errors.length > 0) {
        return err(errors[0])
      }
      return ok({ phase: 'PREFLIGHT', stepId: STEP_ID, value: report })
    },
  }
}
